/**
 * מדיניות פרטיות — TASKS 4.1 · PRD 8.1, 12.3.
 *
 * ⭐ 02.09.2026 — הוחלף בנוסח המאושר של עורך הדין (V2, `Legal Documents/V2`).
 *    ההעברה שמרה על: תוכן העניינים והעוגנים הפנימיים, קישורי `mailto:`,
 *    קישור לעמוד "צור קשר", וקישורי מדיניות הפרטיות של הספקים החיצוניים.
 *    ⛔ העיצוב לא הועבר — רק התוכן הסמנטי.
 *
 * 🔴 **דרישת PRD 12.3 — לא ניתנת לוויתור:** המדיניות **חייבת** לציין במפורש
 *    שהטלפון מועבר ל-Meta לצורך שליחת ההצעה בוואטסאפ (סעיף 4.2 + שורה בטבלת
 *    צד ג'). בלי זה, סימון ה-checkbox בטופס הבקשה (4.3) חסר כיסוי משפטי.
 *
 * ⭐ 03.09.2026 — עו"ד אישר את הנוסח (V2). תיבת האזהרה ("טיוטה") הוסרה
 *    לפי אישור אריאנה. פריט פתוח בנפרד מול טל: פר-5 (הסרת "סליקת תשלומים"
 *    מרשימת מטרות השימוש — אין סליקה מקוונת באתר) — ⛔ לא חוסם.
 */
import { Link } from 'react-router-dom';
import { COMPANY_ADDRESS, COMPANY_LEGAL_NAME, EMAIL, EMAIL_HREF } from '../content/businessInfo.js';

const LAST_REVIEWED = '02.09.2026';

const TOC: { id: string; label: string }[] = [
  { id: 'איסוף-מידע', label: '1. איסוף מידע' },
  { id: 'מטרות-השימוש-במידע', label: '2. מטרות השימוש במידע' },
  { id: 'אבטחת-מידע', label: '3. אבטחת מידע' },
  {
    id: 'העברת-מידע-לצד-שלישי---שקיפות-תיקון-13',
    label: '4. העברת מידע לצד שלישי - שקיפות (תיקון 13)',
  },
  { id: 'עוגיות-cookies-וטכנולוגיות-מעקב', label: '5. עוגיות (Cookies) וטכנולוגיות מעקב' },
  { id: 'זכויות-המשתמש', label: '6. זכויות המשתמש' },
  { id: 'עדכונים-למדיניות', label: '7. עדכונים למדיניות' },
  { id: 'ניהול-הגנת-הפרטיות-ויצירת-קשר', label: '8. ניהול הגנת הפרטיות ויצירת קשר' },
];

/** קישור למדיניות פרטיות של ספק צד שלישי — נפתח בכרטיסייה חדשה. */
function ProviderPolicyLink({ href, provider }: { href: string; provider: string }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-link underline">
      מדיניות הפרטיות של {provider}
    </a>
  );
}

export function PrivacyPage() {
  return (
    <div className="mx-auto max-w-3xl px-6 py-16">
      <h1 className="text-3xl font-bold">מדיניות פרטיות</h1>

      <p className="text-muted mt-2 text-sm">
        {COMPANY_LEGAL_NAME} · כתובת רשומה: {COMPANY_ADDRESS} · דוא"ל לפניות:{' '}
        <a href={EMAIL_HREF} className="ltr-inline text-link underline">
          {EMAIL}
        </a>
      </p>

      <nav
        aria-labelledby="toc-heading"
        className="border-line bg-surface mt-8 rounded-md border p-4"
      >
        <h2 id="toc-heading" className="text-lg font-bold">
          תוכן עניינים
        </h2>
        <ul className="mt-2 list-inside list-disc space-y-1 text-sm">
          {TOC.map((t) => (
            <li key={t.id}>
              <a href={`#${t.id}`} className="text-link underline">
                {t.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <div className="mt-8 space-y-6 leading-relaxed">
        <p className="text-muted">
          <strong className="text-ink">{COMPANY_LEGAL_NAME}</strong> (להלן: "העסק") מחויב להגן על
          פרטיותך. מדיניות זו מפרטת את אופן איסוף והשימוש במידע, בהתאם לחוק הגנת הפרטיות,
          התשמ"א-1981 ותקנותיו. לכל שאלה ניתן לפנות אלינו:{' '}
          <a href={EMAIL_HREF} className="ltr-inline text-link underline">
            {EMAIL}
          </a>
          .
        </p>

        <section>
          <h2 id="איסוף-מידע" className="text-xl font-bold">
            1. איסוף מידע
          </h2>
          <h3 className="text-ink mt-3 font-semibold">סוגי המידע שאנו אוספים</h3>
          <p className="text-muted mt-2">
            1.1. <strong className="text-ink">שקיפות באיסוף מידע:</strong> בשל אופי השירות ופעילות
            האתר, אנו אוספים ומעבדים את המידע הבא:
          </p>
          <ul className="text-muted mt-2 list-inside list-disc space-y-1">
            <li>שם מלא</li>
            <li>כתובת דוא"ל</li>
            <li>מספר טלפון</li>
            <li>פרטי הפנייה ובקשת הצעת המחיר (סוג המוצר, כמות, הערות ותוכן הפנייה).</li>
            <li>
              בעת השימוש באתר נאסף באופן אוטומטי מידע טכני ונתוני שימוש, כגון כתובת IP, מזהה מכשיר,
              סוג המכשיר, מערכת ההפעלה וגרסתה, סוג הדפדפן, מועדי השימוש, פעולות שבוצעו, דפים או
              מסכים שנצפו, נתוני תקלות ומידע טכני נוסף הנוגע לאופן השימוש באתר.
            </li>
          </ul>
          <p className="text-muted mt-2">
            1.1.1. <strong className="text-ink">הבהרה משפטית:</strong> בהתאם לתיקון 13 לחוק הגנת
            הפרטיות, מידע טכני כגון כתובת IP, נתוני מיקום ומזהים דיגיטליים נחשב למידע אישי לכל דבר
            ועניין, ואנו מתייחסים אליו בהתאם לרמת האבטחה והפרטיות הנדרשת.
          </p>
          <p className="text-muted mt-2">
            1.2. <strong className="text-ink">חובת מסירת מידע והסכמה (תיקון 13):</strong> לא חלה
            עליך חובה חוקית למסור את המידע, ומסירתו תלויה ברצונך ובהסכמתך המלאה. מובהר כי המידע נאסף
            לצורך המטרות המפורטות בסעיף 2 להלן.
          </p>
          <p className="text-muted mt-2">
            1.3. <strong className="text-ink">תוצאות אי-מסירה:</strong> עם זאת, מסירת המידע הנדרש
            הינה תנאי לטיפול בפנייתך ולהכנת הצעת מחיר עבורך; ללא מסירת המידע לא נוכל לטפל בפנייה.
          </p>
          <p className="text-muted mt-2">
            1.4. <strong className="text-ink">משך שמירת המידע:</strong> אנו שומרים את המידע למשך
            הזמן הנחוץ למימוש המטרות המפורטות במדיניות זו, או לפרק זמן ארוך יותר ככל שנדרש על פי דין
            (למשל לצורכי ניהול ספרים או עילה משפטית). להלן תקופות השמירה העיקריות:
          </p>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th scope="col" className="border-line border p-2 text-right font-semibold">
                    סוג המידע
                  </th>
                  <th scope="col" className="border-line border p-2 text-right font-semibold">
                    תקופת שמירה
                  </th>
                </tr>
              </thead>
              <tbody className="text-muted">
                <tr>
                  <td className="border-line border p-2">מידע פיננסי וחשבוניות</td>
                  <td className="border-line border p-2">7 שנים (בהתאם להוראות ניהול ספרים)</td>
                </tr>
                <tr>
                  <td className="border-line border p-2">מידע טכני ואנליטי</td>
                  <td className="border-line border p-2">עד 24 חודשים מהאיסוף</td>
                </tr>
              </tbody>
            </table>
          </div>
          <h3 id="אחסון-מקומי-בדפדפן" className="text-ink mt-4 font-semibold">
            1.5. אחסון מקומי בדפדפן (Local Storage)
          </h3>
          <p className="text-muted mt-2">
            לצורך תפעול שוטף, עגלת הבקשות והעדפות הנגישות שבחרת נשמרות באחסון המקומי של הדפדפן
            במכשירך בלבד (Local Storage) - ולא בשרתי העסק - עד לשליחת הטופס. ניתן למחוק מידע זה בכל
            עת דרך ניקוי נתוני הדפדפן.
          </p>
          <h3 id="קטינים" className="text-ink mt-4 font-semibold">
            1.6. קטינים
          </h3>
          <p className="text-muted mt-2">
            האתר והשירות אינם מיועדים לקטינים מתחת לגיל 18, והעסק אינו אוסף ביודעין מידע אישי אודות
            קטינים. ככל שייוודע לעסק כי נמסר מידע אישי של קטין ללא הסכמת האחראי עליו, המידע יימחק.
          </p>
        </section>

        <section>
          <h2 id="מטרות-השימוש-במידע" className="text-xl font-bold">
            2. מטרות השימוש במידע
          </h2>
          <p className="text-muted mt-2">2.1. המידע ישמש את העסק למטרות הבאות בלבד:</p>
          <ul className="text-muted mt-2 list-inside list-disc space-y-1">
            <li>אספקת השירות ותפעולו</li>
            <li>ביצוע, עיבוד וניהול הזמנות ורכישות באתר.</li>
            <li>סליקת תשלומים, הפקת חשבוניות וקבלות וניהול חיובים וזיכויים.</li>
            <li>תיאום, טיפול וביצוע משלוחים ואספקת מוצרים.</li>
            <li>יצירת קשר עם המשתמש בקשר להזמנה, לרכישה, למשלוח או לשירות שהתבקש.</li>
            <li>מתן שירות לקוחות, מענה לפניות, בירורים, תלונות ובקשות.</li>
            <li>טיפול בביטולים, החזרות, החלפות וזיכויים.</li>
            <li>אבטחת האתר, מניעת הונאות, שימוש לרעה, התחזות ופעילות בלתי מורשית.</li>
            <li>ביצוע ניתוחים סטטיסטיים, מדידות ובקרה לצורך שיפור השירות והפעילות העסקית.</li>
          </ul>
          <p className="text-muted mt-2">
            2.2. <strong className="text-ink">משך שמירת המידע:</strong> המידע יישמר למשך כל תקופת
            מתן השירות וכל עוד קיימת עילה משפטית לשמירתו.
          </p>
          <p className="text-muted mt-2">
            2.3. <strong className="text-ink">אי-מסירת מידע:</strong> אי-מסירת המידע הנדרש עלולה
            למנוע את הטיפול בפנייה ואת הכנת הצעת המחיר.
          </p>
        </section>

        <section>
          <h2 id="אבטחת-מידע" className="text-xl font-bold">
            3. אבטחת מידע
          </h2>
          <p className="text-muted mt-2">
            3.1. העסק נוקט באמצעי אבטחת מידע מקובלים וסבירים, בהתאם לאופי פעילותו ולמידע המצוי
            ברשותו, במטרה להגן על המידע מפני גישה, שימוש, שינוי, חשיפה, העתקה או מחיקה בלתי מורשים.
          </p>
          <p className="text-muted mt-2">
            3.2. <strong className="text-ink">אמצעי אבטחה עיקריים:</strong> בין היתר מיושמים האמצעים
            הבאים:
          </p>
          <ul className="text-muted mt-2 list-inside list-disc space-y-1">
            <li>הצפנת התקשורת בין הדפדפן לשרת באמצעות פרוטוקול מאובטח (HTTPS/TLS).</li>
            <li>
              אימות אנושי בטפסים (Cloudflare Turnstile) והגבלת קצב בקשות למניעת שימוש לרעה ומתקפות
              אוטומטיות.
            </li>
            <li>הגבלת גישה לסביבת הניהול והרשאות מבוקרות למי שנדרש לכך לצורך מתן השירות בלבד.</li>
          </ul>
          <p className="text-muted mt-2">
            3.3. עם זאת, אין באפשרות העסק להבטיח חסינות מוחלטת מפני חדירה למערכותיו או מפני אירועי
            אבטחת מידע, והמשתמש מודע לכך.
          </p>
        </section>

        <section>
          <h2 id="העברת-מידע-לצד-שלישי---שקיפות-תיקון-13" className="text-xl font-bold">
            4. העברת מידע לצד שלישי - שקיפות (תיקון 13)
          </h2>
          <p className="text-muted mt-2">
            4.1. אנו עשויים לשתף מידע רק עם גופים חיוניים לצורך מתן השירות, וזאת בהתאם למפורט להלן:
          </p>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th scope="col" className="border-line border p-2 text-right font-semibold">
                    קטגוריה
                  </th>
                  <th scope="col" className="border-line border p-2 text-right font-semibold">
                    ספק / זהות
                  </th>
                  <th scope="col" className="border-line border p-2 text-right font-semibold">
                    מיקום גיאוגרפי
                  </th>
                  <th scope="col" className="border-line border p-2 text-right font-semibold">
                    מטרה
                  </th>
                  <th scope="col" className="border-line border p-2 text-right font-semibold">
                    מדיניות פרטיות
                  </th>
                </tr>
              </thead>
              <tbody className="text-muted">
                <tr>
                  <td className="border-line border p-2">אחסון ותשתית</td>
                  <td className="border-line border p-2">Hostinger</td>
                  <td className="border-line border p-2">האיחוד האירופי</td>
                  <td className="border-line border p-2">אחסון האתר ומסד הנתונים, גיבוי ואבטחה</td>
                  <td className="border-line border p-2">
                    <ProviderPolicyLink
                      href="https://www.hostinger.com/legal/privacy-policy"
                      provider="Hostinger"
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border-line border p-2">אימות אנושי (Anti-Bot)</td>
                  <td className="border-line border p-2">Cloudflare Turnstile</td>
                  <td className="border-line border p-2">ארה"ב / גלובלי</td>
                  <td className="border-line border p-2">
                    אימות שהפנייה מבוצעת על ידי אדם ומניעת שימוש לרעה בטפסים
                  </td>
                  <td className="border-line border p-2">
                    <ProviderPolicyLink
                      href="https://www.cloudflare.com/privacypolicy/"
                      provider="Cloudflare"
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border-line border p-2">משלוח דוא"ל</td>
                  <td className="border-line border p-2">ספק שירותי דוא"ל (SMTP)</td>
                  <td className="border-line border p-2">האיחוד האירופי / ארה"ב</td>
                  <td className="border-line border p-2">משלוח הצעת המחיר והתכתובת ללקוח בדוא"ל</td>
                  <td className="border-line border p-2">יש לעיין במדיניות הפרטיות של הספק</td>
                </tr>
                <tr>
                  <td className="border-line border p-2">הודעות ותקשורת</td>
                  <td className="border-line border p-2">WhatsApp / Meta Platforms Ireland Ltd.</td>
                  <td className="border-line border p-2">אירלנד / ארה"ב</td>
                  <td className="border-line border p-2">
                    משלוח הצעת המחיר בוואטסאפ - רק לפי בחירה מפורשת של המשתמש בטופס
                  </td>
                  <td className="border-line border p-2">
                    <ProviderPolicyLink
                      href="https://www.whatsapp.com/legal/privacy-policy"
                      provider="WhatsApp"
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border-line border p-2">פרסום ושיווק (מתוכנן)</td>
                  <td className="border-line border p-2">Google Ads, Meta Ads</td>
                  <td className="border-line border p-2">האיחוד האירופי (אירלנד) / ארה"ב</td>
                  <td className="border-line border p-2">
                    מדידת קמפיינים ופרסום - רק לאחר חיבור השירות וקבלת הסכמה בבאנר
                  </td>
                  <td className="border-line border p-2">
                    <ProviderPolicyLink
                      href="https://policies.google.com/privacy"
                      provider="Google"
                    />
                    {' · '}
                    <ProviderPolicyLink
                      href="https://www.facebook.com/privacy/policy/"
                      provider="Meta"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-muted mt-3">
            4.2.{' '}
            <strong className="text-ink">
              העברת מספר טלפון לוואטסאפ (Meta) - לפי בחירת המשתמש:
            </strong>{' '}
            כאשר המשתמש מסמן בטופס בקשת הצעת המחיר שברצונו לקבל את ההצעה גם בוואטסאפ, מספר הטלפון
            שמסר מועבר לפלטפורמת WhatsApp / Meta (שרתים מחוץ לישראל, לרבות באירלנד ובארה"ב) לצורך
            פתיחת השיחה ומשלוח ההצעה בלבד. ההעברה מתבצעת אך ורק לפי סימון מפורש ויזום של המשתמש; ללא
            סימון כאמור, לא מועבר מידע לוואטסאפ.
          </p>
          <p className="text-muted mt-2">
            4.3. <strong className="text-ink">אחריות ומיזעור סיכונים:</strong> מידע המועבר לצדדים
            שלישיים יועבר ככל הניתן בצורה אנונימית או מקודדת (Hashing). העסק נשאר מחויב ואחראי כלפי
            המשתמש על אופן הטיפול במידע על ידי ספקים אלו בהתאם לדין הישראלי.
          </p>
          <p className="text-muted mt-2">
            4.4. <strong className="text-ink">הבהרה משפטית:</strong> השימוש בשירותים אלו כפוף
            למדיניות הפרטיות ותנאי השימוש של כל ספק בנפרד. אנו ממליצים לעיין במדיניות הפרטיות של
            הספקים הרלוונטיים.
          </p>
        </section>

        <section>
          <h2 id="עוגיות-cookies-וטכנולוגיות-מעקב" className="text-xl font-bold">
            5. עוגיות (Cookies) וטכנולוגיות מעקב
          </h2>
          <p className="text-muted mt-2">
            5.1. באתר זה נעשה שימוש בקבצי Cookies, פיקסלים, סקריפטים ומזהים דומים (להלן יחד:
            "עוגיות") לצורך תפעול שוטף, אבטחת מידע ושיפור חווית המשתמש.
          </p>
          <p className="text-muted mt-2">
            5.2. <strong className="text-ink">קטגוריות העוגיות ובאנר ההסכמה:</strong> באתר קיים באנר
            הסכמת עוגיות (opt-in) המחלק את העוגיות לשלוש קטגוריות:
          </p>
          <ul className="text-muted mt-2 list-inside list-disc space-y-1">
            <li>
              <strong className="text-ink">חיוניות</strong> - נדרשות לתפעול האתר ולמנגנוני אבטחה
              בסיסיים, ואינן ניתנות לכיבוי.
            </li>
            <li>
              <strong className="text-ink">אנליטיקה</strong> - מדידה וניתוח ביצועים לשיפור השירות.
            </li>
            <li>
              <strong className="text-ink">פרסום</strong> - התאמת פרסום ומדידת קמפיינים.
            </li>
          </ul>
          <p className="text-muted mt-2">
            5.3. <strong className="text-ink">מנגנון ההסכמה:</strong> עוגיות אנליטיקה ופרסום
            (לא-חיוניות) נטענות אך ורק לאחר הסכמה מפורשת שלך בבאנר. האתר עושה שימוש ב-Google Consent
            Mode v2, כאשר ברירת המחדל היא שכל הקטגוריות הלא-חיוניות חסומות (Denied) עד לקבלת הסכמתך.
            ניתן לשנות או לבטל את ההסכמה בכל עת דרך הבאנר.
          </p>
          <p className="text-muted mt-2">
            5.4. <strong className="text-ink">מצב נוכחי:</strong> נכון למועד עדכון מדיניות זו, האתר
            עושה שימוש באחסון מקומי בדפדפן בלבד (עגלת בקשות והעדפות נגישות). עוגיות אנליטיקה ופרסום
            ייטענו רק לאחר הסכמה בבאנר, ורק עם חיבור שירותי הפרסום המתוכננים (Google Ads / Meta
            Ads).
          </p>
          <p className="text-muted mt-2">
            5.5. <strong className="text-ink">שליטה וחסימה:</strong> בנוסף לבאנר, ניתן לחסום או
            למחוק עוגיות בכל עת דרך הגדרות הדפדפן (Chrome, Safari, Firefox, Edge). חסימת עוגיות
            חיוניות עלולה לפגוע בתפקוד חלקים מהאתר.
          </p>
          <p className="text-muted mt-2">
            5.6. <strong className="text-ink">עוגיות צד שלישי:</strong> שירותי צד שלישי המשולבים
            באתר עשויים להציב עוגיות בדפדפן שלך ולעבד מידע (לרבות מזהי דפדפן, כתובות IP ונתוני
            שימוש) למטרותיהם, כמפורט בפרק העברת מידע לצד שלישי במדיניות זו. חלק מהספקים עשויים לפעול
            בשרתים מחוץ לגבולות מדינת ישראל; העברת המידע אליהם מתבצעת בהתאם להוראות חוק הגנת
            הפרטיות, התשמ"א-1981 ותקנותיו.
          </p>
        </section>

        <section>
          <h2 id="זכויות-המשתמש" className="text-xl font-bold">
            6. זכויות המשתמש
          </h2>
          <p className="text-muted mt-2">
            6.1. חוק הגנת הפרטיות, התשמ"א-1981, מקנה לך את הזכויות הבאות:
          </p>
          <ul className="text-muted mt-2 list-inside list-disc space-y-1">
            <li>
              <strong className="text-ink">זכות העיון (סעיף 13 לחוק):</strong> זכותך לעיין במידע
              המוחזק עליך במאגר המידע שלנו.
            </li>
            <li>
              <strong className="text-ink">זכות התיקון (סעיף 14 לחוק):</strong> במידה ומצאת כי המידע
              עליך אינו נכון, שלם, ברור או מעודכן, הנך רשאי/ת לפנות אלינו בבקשה לתקן את המידע או
              למחקו (בכפוף להוראות הדין).
            </li>
            <li>
              <strong className="text-ink">מניעת דואר זבל (סעיף 30א לחוק התקשורת):</strong> שליחת
              חומר פרסומי תעשה אך ורק בכפוף לקבלת הסכמה מפורשת לשיווק כנדרש בחוק. מובהר בזאת כי
              עומדת לך הזכות לבקש הסרה מרשימת התפוצה בכל עת, בקלות וללא עלות. ההסרה תתאפשר באותה דרך
              בה נשלח המסר (למשל: השבת "הסר" או "Remove" בהודעות SMS, או לחיצה על הקישור הייעודי
              בתחתית כל דוא"ל שיווקי), או באמצעות פנייה ישירה לכתובת {EMAIL} שתגרור מחיקה מיידית
              מהמאגר.
            </li>
            <li>
              <strong className="text-ink">מאגר 'אל תתקשר':</strong> מובהר כי בנוסף לזכויותייך
              מולנו, עומדת לך הזכות להירשם במאגר 'אל תתקשר' של הרשות להגנת הצרכן והסחר ההוגן, המגביל
              שיחות שיווק טלפוניות מחברות הפועלות בהתאם לדין.
            </li>
            <li>
              <strong className="text-ink">זכות להגיש תלונה לרשות:</strong> ככל שאתה סבור כי פרטיותך
              נפגעה, עומדת לך הזכות להגיש תלונה לרשות להגנת הפרטיות במשרד המשפטים.
            </li>
          </ul>
          <p className="text-muted mt-2">
            6.2. <strong className="text-ink">אופן מימוש הזכויות:</strong> בקשות למימוש זכויות ניתן
            להפנות אלינו לכתובת הדוא"ל:{' '}
            <a href={EMAIL_HREF} className="ltr-inline text-link underline">
              {EMAIL}
            </a>
            .
          </p>
          <p className="text-muted mt-2">
            6.3. <strong className="text-ink">סעיף 17 לחוק:</strong> מנהל המאגר אחראי על אבטחת המידע
            בו. אנו פועלים בהתאם להוראות החוק והתקנות לאבטחת המידע.
          </p>
        </section>

        <section>
          <h2 id="עדכונים-למדיניות" className="text-xl font-bold">
            7. עדכונים למדיניות
          </h2>
          <p className="text-muted mt-2">
            7.1. אנו עשויים לעדכן מדיניות זו מעת לעת. שינויים מהותיים יפורסמו באתר ו/או יישלחו
            בהודעה ישירה.
          </p>
          <p className="text-muted mt-2">
            7.2. המשך השימוש בשירותים לאחר פרסום שינויים מהווה הסכמה למדיניות המעודכנת.
          </p>
          <p className="text-muted mt-2">7.3. מומלץ לעיין במדיניות זו מעת לעת לבדיקת עדכונים.</p>
        </section>

        <section>
          <h2 id="ניהול-הגנת-הפרטיות-ויצירת-קשר" className="text-xl font-bold">
            8. ניהול הגנת הפרטיות ויצירת קשר
          </h2>
          <p className="text-muted mt-2">
            8.1. העסק פועל בהתאם להוראות הדין למנוע פגיעה בפרטיות הלקוחות.
          </p>
          <p className="text-muted mt-2">
            8.2. לכל שאלה, הבהרה או בקשה בנושא פרטיות, ניתן לפנות אלינו בכתובת:{' '}
            <a href={EMAIL_HREF} className="ltr-inline text-link underline">
              {EMAIL}
            </a>{' '}
            או דרך עמוד{' '}
            <Link to="/contact" className="text-link underline">
              צור קשר
            </Link>
            .
          </p>
          <p className="text-muted mt-2">
            8.3. <strong className="text-ink">זמן תגובה:</strong> אנו מתחייבים להשיב לפניות בנושאי
            פרטיות ומימוש זכויות (עיון/תיקון/מחיקה) תוך פרק זמן סביר (עד 30 יום), בהתאם לדרישות
            הדין.
          </p>
        </section>

        <p className="text-muted text-sm">
          © כל הזכויות שמורות ל-{COMPANY_LEGAL_NAME}, 2026. מדיניות זו עודכנה לאחרונה בתאריך:{' '}
          {LAST_REVIEWED}.
        </p>
      </div>
    </div>
  );
}
